/**
 * PostHog Configuration for AraSync Frontend
 *
 * Product analytics ONLY: which routes are used, by which role, how often.
 * No session recording, no autocapture, no personal identifiers.
 *
 * WHY THIS IS SO RESTRICTIVE
 *
 * This is an EHR admin UI. Every clinical screen is full of PHI, so anything
 * that ships DOM content, element text, or a raw URL to a third-party cloud is
 * a disclosure. The previous configuration claimed three protections in its
 * docstring and implemented none of them: session recording ran with
 * `maskAllInputs: false` and opt-IN masking, `autocapture` sent the text of
 * every clicked element, and users were identified by email with `$name` and
 * `$email` attached.
 *
 * Masking is best-effort. On a screen where nearly every element is PHI, one
 * missed selector is a breach, so recording is OFF rather than masked. If
 * recording is ever wanted, it needs a signed BAA with the processor first, and
 * a deliberate decision — not a config flag flipped in passing.
 *
 * URLs are identifiers here too: /patients/<uuid> ties a session to one person,
 * and query strings have carried names and dates of birth. Both are stripped
 * before capture, leaving the route shape, which is the part worth measuring.
 *
 * STAFF NAMES ARE A SEPARATE QUESTION FROM PHI. Staff are employees, not
 * patients, and an organisation naming its own workforce in its own analytics
 * is ordinary workforce reporting. So names ARE sent, but only for
 * organisations explicitly listed in VITE_POSTHOG_NAMED_ORGS. This platform is
 * multi-tenant: one tenant's staff directory must not leak into an analytics
 * account because another tenant enabled a feature. Unlisted orgs stay
 * pseudonymous, and an empty list names nobody.
 */

import posthog from 'posthog-js';

/**
 * Init options. Exported so the privacy guarantees are testable rather than
 * buried in a call — a future edit that re-enables recording fails a test.
 */
export const POSTHOG_OPTIONS = Object.freeze({
  // Route-level analytics is the whole feature.
  capture_pageview: true,
  capture_pageleave: true,

  // OFF: captures the text of clicked elements, which on this app means
  // patient names on links, buttons and table rows.
  autocapture: false,

  // OFF: no DOM recording of a PHI-bearing UI to a third party.
  // Deliberately the blunt switch, not a masking config.
  disable_session_recording: true,

  // OFF: three optional side bundles PostHog lazy-loads by default. Each was
  // requesting a script from us-assets.i.posthog.com and 404ing on every page
  // load, and none of them is wanted here:
  //   web-vitals.js               page performance telemetry nobody reads
  //   surveys.js                  renders third-party UI inside a clinical app
  //   dead-clicks-autocapture.js  DOM interaction capture, the same class of
  //                               thing autocapture is disabled for above
  // Turning them off removes the console noise and, more to the point, stops a
  // PHI-bearing UI loading third-party behaviour nobody asked for.
  // posthog-js defaults this to "fallback": it requests the VERSIONED asset
  // path /static/<lib-version>/<name>.js first, and only on failure retries
  // the unversioned path. PostHog's CDN does not serve the versioned path for
  // any version we tested, so every page load fired a 404 that the library
  // then quietly recovered from. Setting it false goes straight to the
  // unversioned URL, which returns 200, so the probe never happens.
  strict_script_versioning: false,

  capture_performance: false,
  disable_surveys: true,
  capture_dead_clicks: false,

  // No person profile for anonymous traffic; only identified staff.
  person_profiles: 'identified_only',

  // Strip identifiers out of the URL before anything is sent.
  sanitize_properties: (properties) => {
    const clean = { ...properties };
    ['$current_url', '$referrer', '$pathname'].forEach((k) => {
      if (typeof clean[k] === 'string') clean[k] = sanitizeAnalyticsUrl(clean[k]);
    });
    return clean;
  },
});

/**
 * Organisations whose staff may be identified by name, from
 * VITE_POSTHOG_NAMED_ORGS (comma-separated organizationIds). Deliberately
 * configuration rather than a hardcoded id: this is shared multi-tenant code,
 * and the owning org should not be baked into it.
 */
export function namedOrgIdsFromEnv() {
  const raw = import.meta.env.VITE_POSTHOG_NAMED_ORGS;
  return typeof raw === 'string' && raw.trim()
    ? raw.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
}

/** Path segments that identify a person: uuids and bare numeric ids. */
const ID_SEGMENT = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{3,})$/i;
/**
 * Path segments that are emailed link credentials (/cm-sign/<token>,
 * /rp-sign/<token>, ...): long opaque base64url strings. Route names are short
 * words, so nothing legitimate is this long.
 */
const TOKEN_SEGMENT = /^[A-Za-z0-9_-]{32,}$/;

/**
 * Reduce a URL to its route shape.
 *
 * Drops the query string and hash outright (names and DOBs have appeared in
 * both) and replaces identifying path segments with `:id`. Returns the input
 * unchanged if it cannot be parsed — never throws, because this runs inside
 * PostHog's capture path.
 *
 * @param {string} url
 * @returns {string}
 */
export function sanitizeAnalyticsUrl(url) {
  if (typeof url !== 'string' || !url) return url ?? '';
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    const path = parsed.pathname
      .split('/')
      .map((seg) => (ID_SEGMENT.test(seg) ? ':id' : TOKEN_SEGMENT.test(seg) ? ':token' : seg))
      .join('/');
    // Query and hash are dropped entirely, not sanitized key by key — an
    // allowlist would silently leak the next parameter someone adds.
    return parsed.origin === 'https://placeholder.invalid' ? path : `${parsed.origin}${path}`;
  } catch {
    return url;
  }
}

/**
 * The identity and properties for a logged-in staff member.
 *
 * Returns null when there is no stable id, so a caller cannot fall back to
 * email — that fallback is what put addresses into PostHog in the first place.
 * Properties are built explicitly rather than spread, so a future field on the
 * user object cannot leak: at most userId, role, organizationId, and — for an
 * opted-in org only — $name and $email.
 *
 * @param {object|null} user
 * @param {{namedOrgIds?: string[]}} [options] organisations allowed to be named
 * @returns {{distinctId: string, properties: object}|null}
 */
export function buildUserProperties(user, { namedOrgIds } = {}) {
  const id = user && typeof user.id === 'string' ? user.id.trim() : '';
  if (!id) return null;

  const properties = {
    userId: id,
    role: user.role,
    organizationId: user.organizationId,
  };

  // Name the staff member only for an org that has opted in. Note the identity
  // is still the uuid — the name is a property, so a later change of policy
  // does not orphan the person's history behind a new distinct_id.
  const allowed = Array.isArray(namedOrgIds) ? namedOrgIds : [];
  if (user.organizationId && allowed.includes(user.organizationId)) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (name) properties.$name = name;
    if (user.email) properties.$email = user.email;
  }

  return { distinctId: id, properties };
}

/** Initialize PostHog analytics. */
export function initPostHog() {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  if (!apiKey) return;

  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.posthog.com';

  try {
    posthog.init(apiKey, {
      api_host: host,
      ...POSTHOG_OPTIONS,
      loaded: (ph) => {
        // Fail closed in development. The local backend runs against the
        // PRODUCTION database, so dev traffic is real patient data.
        if (import.meta.env.DEV && !import.meta.env.VITE_POSTHOG_DEV) {
          ph.opt_out_capturing();
        }
      },
    });
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
  }
}

/**
 * Identify a staff member after login.
 *
 * Name and email are sent only for organisations listed in
 * VITE_POSTHOG_NAMED_ORGS. Everyone else stays pseudonymous.
 *
 * @param {Object} user - User object from authentication
 */
export function identifyPostHogUser(user) {
  const identity = buildUserProperties(user, { namedOrgIds: namedOrgIdsFromEnv() });
  if (!identity) {
    posthog.reset();
    return;
  }
  posthog.identify(identity.distinctId, identity.properties);

  // Register organizationId as a super-property so it is attached to every
  // subsequent EVENT (not just the person profile). PostHog does not copy
  // person properties onto events, and the usage-analytics HogQL query
  // filters on `properties.organizationId` — without this, that filter
  // always returns empty.
  if (identity.properties.organizationId) {
    posthog.register({ organizationId: identity.properties.organizationId });
  }
}

/** Reset PostHog user on logout. */
export function resetPostHogUser() {
  posthog.reset();
}
